import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../../utils/apiConfig';

const StatisticsContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const RefreshButton = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.gradient || 'linear-gradient(90deg, #667eea, #764ba2)'};
  }
`;

const StatIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatChange = styled.div`
  font-size: 0.8rem;
  margin-top: 0.5rem;
  color: ${props => props.positive ? '#2ecc71' : '#e74c3c'};
  font-weight: 600;
`;

const ChartsSection = styled.div`
  margin-top: 2rem;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
`;

const ChartCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #374151;
`;

const ChartPlaceholder = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 8px;
  padding: 1rem;
  color: #e74c3c;
  margin-bottom: 1rem;
`;

const Statistics = ({ onBack }) => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalBookings: 0,
    activeBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    cancelledBookings: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    averageBookingValue: 0
  });

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      // Lade alle Statistiken parallel
      const [usersResponse, coursesResponse, bookingsResponse] = await Promise.all([
        fetch(getApiUrl('/admin/users'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/admin/courses'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/admin/bookings'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const [usersData, coursesData, bookingsData] = await Promise.all([
        usersResponse.json(),
        coursesResponse.json(),
        bookingsResponse.json()
      ]);

      if (usersData.success && coursesData.success && bookingsData.success) {
        const users = usersData.data || [];
        const courses = coursesData.data || [];
        const bookings = bookingsData.data || [];

        // Berechne Statistiken
        const totalUsers = users.length;
        const totalCourses = courses.length;
        const totalBookings = bookings.length;
        
        const activeBookings = bookings.filter(b => b.status === 'confirmed').length;
        const pendingBookings = bookings.filter(b => b.status === 'pending').length;
        const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
        const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;
        
        const totalRevenue = bookings.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
        const monthlyRevenue = bookings
          .filter(b => {
            const bookingDate = new Date(b.created_at);
            const now = new Date();
            return bookingDate.getMonth() === now.getMonth() && 
                   bookingDate.getFullYear() === now.getFullYear();
          })
          .reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
        
        const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;

        setStats({
          totalUsers,
          totalCourses,
          totalBookings,
          activeBookings,
          pendingBookings,
          confirmedBookings,
          cancelledBookings,
          totalRevenue,
          monthlyRevenue,
          averageBookingValue
        });
      } else {
        setError('Fehler beim Laden der Statistiken');
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  if (loading) {
    return (
      <StatisticsContainer>
        <Header>
          <HeaderLeft>
            <BackButton onClick={onBack}>
              ← Zurück zum Dashboard
            </BackButton>
            <Title>Statistiken</Title>
          </HeaderLeft>
        </Header>
        <LoadingSpinner>Statistiken werden geladen...</LoadingSpinner>
      </StatisticsContainer>
    );
  }

  return (
    <StatisticsContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            ← Zurück zum Dashboard
          </BackButton>
          <Title>Statistiken</Title>
        </HeaderLeft>
        <RefreshButton onClick={loadStatistics}>
          🔄 Aktualisieren
        </RefreshButton>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          gradient="linear-gradient(90deg, #3498db, #2980b9)"
        >
          <StatIcon>👥</StatIcon>
          <StatValue>{formatNumber(stats.totalUsers)}</StatValue>
          <StatLabel>Gesamt Benutzer</StatLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          gradient="linear-gradient(90deg, #2ecc71, #27ae60)"
        >
          <StatIcon>🎠</StatIcon>
          <StatValue>{formatNumber(stats.totalCourses)}</StatValue>
          <StatLabel>Aktive Kurse</StatLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          gradient="linear-gradient(90deg, #9b59b6, #8e44ad)"
        >
          <StatIcon>📋</StatIcon>
          <StatValue>{formatNumber(stats.totalBookings)}</StatValue>
          <StatLabel>Gesamt Buchungen</StatLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          gradient="linear-gradient(90deg, #f39c12, #e67e22)"
        >
          <StatIcon>✅</StatIcon>
          <StatValue>{formatNumber(stats.confirmedBookings)}</StatValue>
          <StatLabel>Bestätigte Buchungen</StatLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          gradient="linear-gradient(90deg, #e74c3c, #c0392b)"
        >
          <StatIcon>💰</StatIcon>
          <StatValue>{formatCurrency(stats.totalRevenue)}</StatValue>
          <StatLabel>Gesamtumsatz</StatLabel>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          gradient="linear-gradient(90deg, #1abc9c, #16a085)"
        >
          <StatIcon>📊</StatIcon>
          <StatValue>{formatCurrency(stats.monthlyRevenue)}</StatValue>
          <StatLabel>Monatsumsatz</StatLabel>
        </StatCard>
      </StatsGrid>

      <ChartsSection>
        <ChartsGrid>
          <ChartCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <ChartTitle>📈 Buchungsstatus Verteilung</ChartTitle>
            <ChartPlaceholder>
              Bestätigt: {stats.confirmedBookings} | 
              Ausstehend: {stats.pendingBookings} | 
              Storniert: {stats.cancelledBookings}
            </ChartPlaceholder>
          </ChartCard>

          <ChartCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <ChartTitle>💹 Durchschnittlicher Buchungswert</ChartTitle>
            <ChartPlaceholder>
              {formatCurrency(stats.averageBookingValue)} pro Buchung
            </ChartPlaceholder>
          </ChartCard>
        </ChartsGrid>
      </ChartsSection>
    </StatisticsContainer>
  );
};

export default Statistics;
